import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Loader2, Eye, EyeOff, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ModuleRenderer } from "@/pages/ModularPage";

const API = getApiUrl();

/**
 * ModuleInstancesPreview
 * Shows a live preview of every module instance defined in the system so the
 * admin can immediately see how their current Site Styles look across every
 * piece of the public site. Each instance renders inside a clipped card with
 * a title, type badge and enabled state — clicking the eye toggle lets the
 * admin temporarily hide an instance from the preview wall.
 */
export default function ModuleInstancesPreview() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyEnabled, setShowOnlyEnabled] = useState(true);
  const [hiddenLocal, setHiddenLocal] = useState({});

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/module-instances`, {
        withCredentials: true,
      });
      setInstances(r.data?.instances || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInstances(); }, []);

  const filtered = useMemo(() => {
    let list = instances;
    if (showOnlyEnabled) list = list.filter((i) => i.enabled !== false);
    list = list.filter((i) => !hiddenLocal[i.instance_id]);
    return list;
  }, [instances, showOnlyEnabled, hiddenLocal]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const inst of filtered) {
      const key = inst.module_type || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inst);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <section className="space-y-4 pb-8 border-t pt-8 mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold">Module Instance Previews</h3>
          <p className="text-sm text-muted-foreground">
            See how the current Site Styles look applied to every module instance.
            Disabled instances are hidden by default.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="only-enabled"
              data-testid="preview-only-enabled-toggle"
              checked={showOnlyEnabled}
              onCheckedChange={setShowOnlyEnabled}
            />
            <Label htmlFor="only-enabled" className="text-sm">Only enabled</Label>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInstances} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : grouped.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-2 border-dashed">
          No module instances to preview. Create some in CMS → Module Instances.
        </Card>
      ) : (
        <div className="space-y-6" data-public-site>
          {grouped.map(([type, list]) => (
            <div key={type} className="space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {type.replace(/_/g, " ")} ({list.length})
                </h4>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {list.map((inst) => (
                  <InstancePreviewCard
                    key={inst.instance_id}
                    instance={inst}
                    onHide={() =>
                      setHiddenLocal((p) => ({ ...p, [inst.instance_id]: true }))
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function InstancePreviewCard({ instance, onHide }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/module-instances/${instance.instance_id}/usage`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setUsage(r.data?.used_in || []);
      } catch {
        if (!cancelled) setUsage([]);
      }
    })();
    return () => { cancelled = true; };
  }, [instance.instance_id]);

  const firstPath = usage && usage[0]?.path;

  return (
    <Card
      className="overflow-hidden"
      data-testid={`preview-card-${instance.instance_id}`}
    >
      <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/40 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate">{instance.name}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wide">
            {(instance.module_type || "").replace(/_/g, " ")}
          </span>
          {instance.is_default && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
              default
            </span>
          )}
          {instance.enabled === false && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              disabled
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {firstPath ? (
            <a
              href={firstPath}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`preview-link-${instance.instance_id}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border hover:bg-primary/5 hover:border-primary/40 transition-colors"
              title={`Open on site: ${firstPath}${usage.length > 1 ? ` (+${usage.length - 1} more)` : ""}`}
            >
              <ExternalLink className="h-3 w-3" />
              View on site
              {usage.length > 1 && (
                <span className="ml-1 text-[10px] text-muted-foreground">×{usage.length}</span>
              )}
            </a>
          ) : usage === null ? (
            <span className="text-[10px] text-muted-foreground px-1">checking…</span>
          ) : (
            <span className="text-[10px] text-muted-foreground px-1">not used yet</span>
          )}
          <Button variant="ghost" size="sm" onClick={onHide} title="Hide from preview wall">
            <EyeOff className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        className="relative max-h-[420px] overflow-auto bg-background"
        // pointer-events:none would make the card uninteractive, but we keep
        // interaction so admins can scroll/inspect the live preview.
      >
        <ModuleRenderer
          module={{
            module_type: instance.module_type,
            instance_id: instance.instance_id,
            config: instance.config,
          }}
          moduleDetail={{
            type: instance.module_type,
            name: instance.name,
            instance_id: instance.instance_id,
            config: instance.config,
          }}
        />
      </div>
      <div className="px-4 py-1.5 border-t text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <Eye className="h-3 w-3" />
        Live preview — reflects current Site Styles
      </div>
    </Card>
  );
}
